import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Card } from "react-bootstrap";
import { Tooltip, IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ethers } from "ethers";
import toast from "react-hot-toast";
import { IoRefresh, IoChevronDown, IoChevronForward } from "react-icons/io5";
import Pagination from "../../Live/components/Pagenation";
import trinityManagerArtifact from "../../../../configs/abis/trinity-manager.json";
import config from "../../../../configs";
import { request } from "../../../../admin/plugins/axios";
import { endpoints } from "../../../../utils/networks";
import MintPanel from "./MintPanel";

const { abi: trinityManagerAbi } = trinityManagerArtifact;
const managerInterface = new ethers.utils.Interface(trinityManagerAbi);
const PAGE_SIZE = 5;

const shorten = (value) =>
  value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "--";

// Component to render address with tooltip and copy button
const AddressWithTooltip = ({
  shortenedAddress,
  fullAddress,
  addressName,
  onCopy,
}) => {
  const handleCopy = (e) => {
    e.stopPropagation();
    if (fullAddress && onCopy) {
      onCopy(fullAddress);
    }
  };

  return (
    <Tooltip
      title={
        <div
          style={{
            padding: "4px 0",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            minWidth: "200px",
          }}
        >
          {addressName && (
            <div
              style={{
                fontSize: "0.95rem",
                fontWeight: 500,
                color: "#fff",
                marginBottom: "4px",
              }}
            >
              {addressName}
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontSize: "0.85rem",
                color: "#bfc3d9",
                fontFamily: "monospace",
                wordBreak: "break-all",
              }}
            >
              {fullAddress || shortenedAddress}
            </span>
            {fullAddress && (
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  color: "#fff",
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </div>
        </div>
      }
      placement="top"
      arrow
    >
      <span
        style={{
          cursor: "pointer",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          color: "#fff",
        }}
      >
        {shortenedAddress}
      </span>
    </Tooltip>
  );
};

// Component to render batch addresses tooltip
const BatchAddressesTooltip = ({
  addresses,
  addressNames,
  onCopy,
  children,
}) => {
  const handleCopy = (e, address) => {
    e.stopPropagation();
    if (address && onCopy) {
      onCopy(address);
    }
  };

  return (
    <Tooltip
      title={
        <div
          style={{
            padding: "4px 0",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            minWidth: "200px",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {addresses.map(({ full, short }, index) => {
            const lowerAddr = full?.toLowerCase();
            const addressName = lowerAddr ? addressNames[lowerAddr] : null;
            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "space-between",
                  paddingBottom: index < addresses.length - 1 ? "8px" : "0",
                  borderBottom:
                    index < addresses.length - 1
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    flex: 1,
                  }}
                >
                  {addressName && (
                    <div
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: 500,
                        color: "#fff",
                        marginBottom: "4px",
                      }}
                    >
                      {addressName}
                    </div>
                  )}
                  <span
                    style={{
                      fontSize: "0.85rem",
                      color: "#bfc3d9",
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                    }}
                  >
                    {full || short}
                  </span>
                </div>
                {full && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleCopy(e, full)}
                    sx={{
                      color: "#fff",
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </div>
            );
          })}
        </div>
      }
      placement="top"
      arrow
      enterDelay={0}
      leaveDelay={300}
      PopperProps={{
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 2], // Reduce distance between tooltip and text
            },
          },
        ],
      }}
    >
      <span
        style={{
          cursor: "pointer",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          color: "#fff",
        }}
      >
        {children}
      </span>
    </Tooltip>
  );
};

// Parse args and return both text and full addresses
const parseArgsWithAddresses = (functionName, data) => {
  if (!data || data === "0x") return { text: "--", addresses: [] };

  const addresses = [];
  let text = parseArgs(functionName, data);

  if (text === "--") return { text, addresses };

  // Extract addresses from decoded data to map shortened -> full
  try {
    let fragment;
    try {
      fragment = managerInterface.getFunction(functionName);
    } catch (e) {
      const altName = functionName.startsWith("_")
        ? functionName.substring(1)
        : `_${functionName}`;
      try {
        fragment = managerInterface.getFunction(altName);
      } catch (e2) {
        return { text, addresses: [] };
      }
    }

    let dataToDecode = data;
    if (data.length > 10 && data.startsWith("0x")) {
      const functionSelector = managerInterface.getSighash(fragment);
      const dataSelector = data.slice(0, 10);
      if (dataSelector.toLowerCase() !== functionSelector.toLowerCase()) {
        dataToDecode = data;
      } else {
        dataToDecode = data;
      }
    }

    let decoded;
    try {
      decoded = managerInterface.decodeFunctionData(fragment, dataToDecode);
    } catch (decodeError) {
      try {
        decoded = ethers.utils.defaultAbiCoder.decode(
          fragment.inputs.map((input) => input.type),
          dataToDecode
        );
      } catch (e) {
        return { text, addresses: [] };
      }
    }

    const normalizedName = functionName.replace(/^_/, "");

    // Extract addresses based on function
    if (
      normalizedName === "adminMintShares" ||
      normalizedName === "_adminMintShares"
    ) {
      const recipient = decoded[0];
      const referrer = decoded[2] || ethers.constants.AddressZero;
      const recipientAddr =
        typeof recipient === "string"
          ? recipient
          : recipient?.toString?.() || recipient;
      const referrerAddr =
        typeof referrer === "string"
          ? referrer
          : referrer?.toString?.() || referrer;
      const isZeroAddress =
        referrerAddr === ethers.constants.AddressZero ||
        referrerAddr === "0x0000000000000000000000000000000000000000";

      addresses.push({ full: recipientAddr, short: shorten(recipientAddr) });
      if (!isZeroAddress) {
        addresses.push({ full: referrerAddr, short: shorten(referrerAddr) });
      }
    } else if (
      normalizedName === "adminBatchAddPurchasesWithoutMint" ||
      normalizedName === "_adminBatchAddPurchasesWithoutMint"
    ) {
      // Extract addresses from arrays: userAddresses[], referrers[]
      const userAddresses = decoded[0] || [];
      const referrers = decoded[2] || [];

      // Process user addresses
      if (Array.isArray(userAddresses)) {
        userAddresses.forEach((addr) => {
          const addrStr =
            typeof addr === "string" ? addr : addr?.toString?.() || addr;
          if (addrStr && addrStr !== ethers.constants.AddressZero) {
            addresses.push({ full: addrStr, short: shorten(addrStr) });
          }
        });
      }

      // Process referrer addresses (skip zero addresses)
      if (Array.isArray(referrers)) {
        referrers.forEach((addr) => {
          const addrStr =
            typeof addr === "string" ? addr : addr?.toString?.() || addr;
          const isZeroAddress =
            addrStr === ethers.constants.AddressZero ||
            addrStr === "0x0000000000000000000000000000000000000000";
          if (addrStr && !isZeroAddress) {
            // Only add if not already in addresses
            const exists = addresses.some(
              (a) => a.full.toLowerCase() === addrStr.toLowerCase()
            );
            if (!exists) {
              addresses.push({ full: addrStr, short: shorten(addrStr) });
            }
          }
        });
      }
    } else if (normalizedName === "setMasterWallet") {
      const wallet = decoded[0];
      const walletAddr =
        typeof wallet === "string" ? wallet : wallet?.toString?.() || wallet;
      addresses.push({ full: walletAddr, short: shorten(walletAddr) });
    } else {
      // For other functions, extract all address parameters
      fragment.inputs.forEach((input, index) => {
        if (input.type === "address" && decoded[index]) {
          const addr =
            typeof decoded[index] === "string"
              ? decoded[index]
              : decoded[index]?.toString?.() || decoded[index];
          if (addr && addr !== ethers.constants.AddressZero) {
            addresses.push({ full: addr, short: shorten(addr) });
          }
        }
      });
    }
  } catch (error) {
    console.warn("Failed to extract addresses from parseArgs", error);
  }

  return { text, addresses };
};

// Render details text with interactive addresses
const renderDetailsText = (text, addresses, addressNames, onCopy) => {
  if (!text || text === "--") return text;

  if (!addresses || addresses.length === 0) {
    // Handle line breaks in plain text
    if (text.includes("\n")) {
      return text.split("\n").map((line, idx) => (
        <React.Fragment key={idx}>
          {line}
          {idx < text.split("\n").length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return text;
  }

  // Create a map of shortened -> full address
  const addressMap = {};
  addresses.forEach(({ full, short }) => {
    addressMap[short] = full;
  });

  // Split text and replace addresses with interactive components
  const parts = [];
  let lastIndex = 0;
  const addressPattern = /0x[a-fA-F0-9]{4,6}\.\.\.[a-fA-F0-9]{4}/g;
  let match;

  while ((match = addressPattern.exec(text)) !== null) {
    // Add text before the address (handle line breaks)
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      const matchIndex = match.index; // Capture match.index for use in key
      if (beforeText.includes("\n")) {
        const lines = beforeText.split("\n");
        lines.forEach((line, idx) => {
          if (line) parts.push(line);
          if (idx < lines.length - 1)
            parts.push(<br key={`br-${matchIndex}-${idx}`} />);
        });
      } else {
        parts.push(beforeText);
      }
    }

    // Add the interactive address component
    const shortened = match[0];
    const fullAddress = addressMap[shortened];
    const lowerAddr = fullAddress?.toLowerCase();
    const addressName = lowerAddr ? addressNames[lowerAddr] : null;

    parts.push(
      <AddressWithTooltip
        key={`addr-${match.index}`}
        shortenedAddress={shortened}
        fullAddress={fullAddress}
        addressName={addressName}
        onCopy={onCopy}
      />
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text, but handle "+X more" specially
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);

    // Check if remaining text contains "+X more" pattern (for batch functions)
    const morePattern = /\s+\+(\d+)\s+more/;
    const moreMatch = remainingText.match(morePattern);

    // If there's a "+X more" pattern, replace it with interactive tooltip
    if (moreMatch && addresses.length > 3) {
      const beforeMore = remainingText.substring(0, moreMatch.index);
      const moreText = moreMatch[0].trim(); // Remove leading space
      const moreCount = parseInt(moreMatch[1], 10);

      // Handle line breaks in beforeMore
      if (beforeMore.includes("\n")) {
        const lines = beforeMore.split("\n");
        lines.forEach((line, idx) => {
          if (line) parts.push(line);
          if (idx < lines.length - 1)
            parts.push(<br key={`br-remaining-${idx}`} />);
        });
      } else if (beforeMore) {
        parts.push(beforeMore);
      }

      // Get only the remaining addresses (skip first 3 that are shown in main text)
      // For batch functions, we show first 3 user addresses in main text
      // So we take addresses from index 3 onwards (only user addresses, not referrers)
      const displayedCount = 3;
      const remainingAddresses = addresses.slice(
        displayedCount,
        displayedCount + moreCount
      );

      // Add tooltip for "+X more" showing only remaining addresses
      parts.push(
        <span key="batch-more-wrapper" style={{ whiteSpace: "nowrap" }}>
          {" "}
          <BatchAddressesTooltip
            addresses={remainingAddresses}
            addressNames={addressNames}
            onCopy={onCopy}
          >
            {moreText}
          </BatchAddressesTooltip>
        </span>
      );
    } else {
      // Handle line breaks in remaining text
      if (remainingText.includes("\n")) {
        const lines = remainingText.split("\n");
        lines.forEach((line, idx) => {
          if (line) parts.push(line);
          if (idx < lines.length - 1)
            parts.push(<br key={`br-remaining-${idx}`} />);
        });
      } else {
        parts.push(remainingText);
      }
    }
  }

  return parts.length > 0 ? <>{parts}</> : text;
};

const parseArgs = (functionName, data) => {
  if (!data || data === "0x") return "--";

  try {
    // Try to get function fragment - handle both with and without underscore
    let fragment;
    try {
      fragment = managerInterface.getFunction(functionName);
    } catch (e) {
      // Try with underscore if original failed
      const altName = functionName.startsWith("_")
        ? functionName.substring(1)
        : `_${functionName}`;
      try {
        fragment = managerInterface.getFunction(altName);
      } catch (e2) {
        console.warn("Function not found:", functionName, altName);
        throw e2;
      }
    }

    // Check if data includes function selector (first 4 bytes = 10 hex chars)
    let dataToDecode = data;
    if (data.length > 10 && data.startsWith("0x")) {
      // Check if first 4 bytes match function selector
      const functionSelector = managerInterface.getSighash(fragment);
      const dataSelector = data.slice(0, 10);
      if (dataSelector.toLowerCase() !== functionSelector.toLowerCase()) {
        // Data doesn't include selector, decode parameters only
        dataToDecode = data;
      } else {
        // Data includes selector, use as is
        dataToDecode = data;
      }
    }

    // Decode the data
    let decoded;
    try {
      decoded = managerInterface.decodeFunctionData(fragment, dataToDecode);
    } catch (decodeError) {
      // If decodeFunctionData fails, try decodeParameters (for data without selector)
      try {
        decoded = ethers.utils.defaultAbiCoder.decode(
          fragment.inputs.map((input) => input.type),
          dataToDecode
        );
      } catch (e) {
        throw decodeError;
      }
    }

    // Format based on function name
    const normalizedName = functionName.replace(/^_/, ""); // Remove leading underscore

    switch (normalizedName) {
      case "adminMintShares":
      case "_adminMintShares": {
        const recipient = decoded[0];
        const shares = decoded[1];
        const referrer = decoded[2] || ethers.constants.AddressZero;
        const sharesNum =
          shares?.toString?.() || shares?.toNumber?.() || shares;
        const recipientAddr =
          typeof recipient === "string"
            ? recipient
            : recipient?.toString?.() || recipient;
        const referrerAddr =
          typeof referrer === "string"
            ? referrer
            : referrer?.toString?.() || referrer;
        const isZeroAddress =
          referrerAddr === ethers.constants.AddressZero ||
          referrerAddr === "0x0000000000000000000000000000000000000000";
        const referrerText = isZeroAddress
          ? ""
          : ` (referrer: ${shorten(referrerAddr)})`;
        return `Mint ${sharesNum} shares to ${shorten(
          recipientAddr
        )}${referrerText}`;
      }
      case "adminBatchAddPurchasesWithoutMint":
      case "_adminBatchAddPurchasesWithoutMint": {
        const userAddresses = decoded[0] || [];
        const shareAmounts = decoded[1] || [];
        // referrers array is extracted but not displayed in text (used in parseArgsWithAddresses)
        // eslint-disable-next-line no-unused-vars
        const referrers = decoded[2] || [];

        const userCount = Array.isArray(userAddresses)
          ? userAddresses.length
          : 0;
        const totalShares = Array.isArray(shareAmounts)
          ? shareAmounts.reduce((sum, shares) => {
              const sharesNum =
                shares?.toString?.() || shares?.toNumber?.() || shares || 0;
              return sum + Number(sharesNum || 0);
            }, 0)
          : 0;

        // Build address list for display (show first 3 only in main text)
        const addressList = [];
        if (Array.isArray(userAddresses) && userAddresses.length > 0) {
          const displayCount = Math.min(userAddresses.length, 3);
          for (let i = 0; i < displayCount; i++) {
            const addr =
              typeof userAddresses[i] === "string"
                ? userAddresses[i]
                : userAddresses[i]?.toString?.() || userAddresses[i];
            if (addr) {
              addressList.push(shorten(addr));
            }
          }
        }

        // Format text with addresses on new line
        const addressText =
          addressList.length > 0 ? `(${addressList.join(", ")})` : "";
        const moreText =
          userAddresses.length > 3 ? ` +${userAddresses.length - 3} more` : "";

        // Return formatted text with line break
        if (addressText) {
          return `Batch add purchases: ${userCount} users, total ${totalShares} shares\n${addressText}${moreText}`;
        } else {
          return `Batch add purchases: ${userCount} users, total ${totalShares} shares${moreText}`;
        }
      }
      case "setMasterWallet": {
        const wallet = decoded[0];
        const walletAddr =
          typeof wallet === "string" ? wallet : wallet?.toString?.() || wallet;
        return `Set master wallet to ${shorten(walletAddr)}`;
      }
      default: {
        const entries = fragment.inputs.map((input, index) => {
          const value = decoded[index];
          let display;
          if (typeof value === "string") {
            display = value;
          } else if (value?.toString) {
            display = value.toString();
          } else if (ethers.BigNumber.isBigNumber(value)) {
            display = value.toString();
          } else {
            display = JSON.stringify(value);
          }
          return `${input.name || `arg${index}`}: ${display}`;
        });
        return entries.join(" | ");
      }
    }
  } catch (error) {
    console.warn("Unable to decode tx data", { functionName, data, error });
    // Try to decode as raw parameters if function name suggests adminMintShares
    if (
      functionName?.includes("adminMintShares") ||
      functionName?.includes("Mint")
    ) {
      try {
        // Try to decode as (address, uint256, address) - new format with referrer
        try {
          const decoded = ethers.utils.defaultAbiCoder.decode(
            ["address", "uint256", "address"],
            data
          );
          const recipient = decoded[0];
          const shares = decoded[1]?.toString?.() || decoded[1];
          const referrer = decoded[2];
          const isZeroAddress =
            referrer === ethers.constants.AddressZero ||
            referrer === "0x0000000000000000000000000000000000000000";
          const referrerText = isZeroAddress
            ? ""
            : ` (referrer: ${shorten(referrer)})`;
          return `Mint ${shares} shares to ${shorten(
            recipient
          )}${referrerText}`;
        } catch (newFormatError) {
          // Fallback to old format (address, uint256) for backward compatibility
          const decoded = ethers.utils.defaultAbiCoder.decode(
            ["address", "uint256"],
            data
          );
          const recipient = decoded[0];
          const shares = decoded[1]?.toString?.() || decoded[1];
          return `Mint ${shares} shares to ${shorten(recipient)}`;
        }
      } catch (fallbackError) {
        console.warn("Fallback decode also failed", fallbackError);
      }
    }
    return "Unable to decode parameters";
  }
};

function MultisigTransactions({
  provider,
  signer,
  currentUser,
  adminAddresses = [],
  refreshKey,
  isOwner,
}) {
  const managerAddress = config.get("smartContract.TRINITY_MANAGER_ADDRESS");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [localRefresh, setLocalRefresh] = useState(0);
  const [addressNames, setAddressNames] = useState({});
  const addressNamesRef = useRef({});
  const [showMintPanel, setShowMintPanel] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // "all", "pending", or "executed"

  useEffect(() => {
    setPage(1);
  }, [refreshKey]);

  const fetchAddressNames = useCallback(async (addresses = []) => {
    if (!addresses.length) return;
    const normalized = Array.from(
      new Set(
        addresses
          .map((addr) => (typeof addr === "string" ? addr.toLowerCase() : null))
          .filter((addr) => addr && !addressNamesRef.current[addr])
      )
    );
    if (!normalized.length) return;
    try {
      const response = await request(
        endpoints.Users.GetNames.method,
        endpoints.Users.GetNames.url,
        { addresses: normalized }
      );
      const users = response?.data?.data?.users || response?.data?.users || [];
      const mapping = {};
      users.forEach((user) => {
        const lower = user?.wallet_address?.toLowerCase?.();
        if (lower) {
          mapping[lower] = user?.name || user?.username || user?.wallet_address;
        }
      });
      if (Object.keys(mapping).length) {
        addressNamesRef.current = { ...addressNamesRef.current, ...mapping };
        setAddressNames((prev) => ({ ...prev, ...mapping }));
      }
    } catch (err) {
      console.error("Failed to fetch user names", err);
    }
  }, []);

  const normalizedAdmins = useMemo(
    () =>
      adminAddresses
        .filter(Boolean)
        .map((addr) => addr.toLowerCase())
        .filter((addr, idx, arr) => arr.indexOf(addr) === idx),
    [adminAddresses]
  );

  const currentUserLower = currentUser?.toLowerCase();
  const derivedIsOwner =
    currentUserLower &&
    normalizedAdmins.some((addr) => addr === currentUserLower);
  const effectiveIsOwner =
    typeof isOwner === "boolean" ? isOwner : !!derivedIsOwner;

  const fetchTransactions = useCallback(async () => {
    if (!provider || !managerAddress || !signer || !effectiveIsOwner) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const contract = new ethers.Contract(
        managerAddress,
        trinityManagerAbi,
        signer
      );
      const contractPage = Math.max(page - 1, 0);
      const [txs, totalList] = await contract.getAllTransactions(
        ethers.BigNumber.from(contractPage),
        ethers.BigNumber.from(PAGE_SIZE)
      );

      console.log("🔍 Raw transactions from contract:", txs);
      console.log("🔍 Total transactions:", totalList?.toString());

      const serialized = txs.map((tx, idx) => {
        const numConfirmations = Number(tx.numConfirmations || 0);
        const confirmedCount = (tx.confirmed || []).length;

        console.log(`📋 Transaction ${idx + 1}:`, {
          from: tx.from,
          numConfirmations: numConfirmations,
          numConfirmationsRaw: tx.numConfirmations?.toString(),
          confirmed: tx.confirmed,
          confirmedCount: confirmedCount,
          functionName: tx.functionName,
          executed: tx.executed,
        });

        return {
          from: tx.from,
          executed: tx.executed,
          numConfirmations: numConfirmations,
          functionName: tx.functionName,
          data: tx.data,
          confirmed: tx.confirmed || [],
          createdAt: Number(tx.createdAt || 0),
          updatedAt: Number(tx.updatedAt || 0),
        };
      });

      setRows(serialized);
      setTotal(Number(totalList || 0));
    } catch (error) {
      console.error("Failed to load multisig transactions", error);
      if (error?.reason === "Page is Out of Range") {
        if (page !== 1) {
          setPage(1);
        } else {
          setRows([]);
          setTotal(0);
          toast("No multisig transactions found yet.");
        }
      } else {
        toast.error("Unable to load multisig transactions");
        setRows([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [managerAddress, page, provider, signer, effectiveIsOwner]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions, refreshKey, localRefresh]);

  const offset = (page - 1) * PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));

  const handleCopy = useCallback((address) => {
    if (!address) return;
    navigator.clipboard
      .writeText(address)
      .then(() => toast.success("Address copied to clipboard"))
      .catch(() => toast.error("Failed to copy address"));
  }, []);

  const rowsWithMeta = rows.map((row, index) => {
    const transactionId = offset + index + 1;
    const approvals = row.confirmed.length;
    const requiredConfirmations = 3; // Fixed requirement: exactly 3 confirmations
    const executed =
      row.executed && row.executed !== ethers.constants.AddressZero;
    const hasApproved =
      currentUserLower &&
      row.confirmed.some(
        (addr) => addr && addr.toLowerCase() === currentUserLower
      );

    // Fetch names for confirmed addresses
    if (row.confirmed && row.confirmed.length > 0) {
      fetchAddressNames(row.confirmed);
    }

    // Fetch name for the submitter (row.from)
    if (row.from) {
      fetchAddressNames([row.from]);
    }

    // Extract addresses from details for fetching names
    const { text, addresses } = parseArgsWithAddresses(
      row.functionName,
      row.data
    );
    const detailAddresses = addresses.map(({ full }) => full).filter(Boolean);
    if (detailAddresses.length > 0) {
      fetchAddressNames(detailAddresses);
    }

    return {
      ...row,
      transactionId,
      approvals,
      numConfirmations: requiredConfirmations, // Force to 3
      executed,
      hasApproved,
      canApprove: effectiveIsOwner && !executed && !hasApproved,
      canExecute:
        effectiveIsOwner &&
        !executed &&
        approvals === requiredConfirmations &&
        requiredConfirmations === 3, // Only show Execute when exactly 3/3
      detailsText: text,
      detailsAddresses: addresses,
    };
  });

  // Filter rows based on active tab
  const filteredRows = useMemo(() => {
    if (activeTab === "executed") {
      return rowsWithMeta.filter((row) => row.executed);
    } else if (activeTab === "pending") {
      // pending - show non-executed transactions
      return rowsWithMeta.filter((row) => !row.executed);
    } else {
      // all - show all transactions
      return rowsWithMeta;
    }
  }, [rowsWithMeta, activeTab]);

  const handleConfirm = async (transactionId) => {
    if (!signer) {
      toast.error("Connect your wallet to approve");
      return;
    }
    setActionId(transactionId);
    try {
      const contract = new ethers.Contract(
        managerAddress,
        trinityManagerAbi,
        signer
      );
      const tx = await contract.confirmTransaction(transactionId);
      toast.loading("Submitting approval...", { id: "multisig-action" });
      await tx.wait();
      toast.success("Approval recorded", { id: "multisig-action" });
      setLocalRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Approval failed", error);
      toast.error(error?.reason || error?.message || "Approval failed", {
        id: "multisig-action",
      });
    } finally {
      setActionId(null);
    }
  };

  const handleExecute = async (transactionId) => {
    if (!signer) {
      toast.error("Connect your wallet to execute");
      return;
    }
    setActionId(transactionId);
    try {
      const contract = new ethers.Contract(
        managerAddress,
        trinityManagerAbi,
        signer
      );
      const tx = await contract.executeTransaction(transactionId);
      toast.loading("Executing transaction...", { id: "multisig-action" });
      await tx.wait();
      toast.success("Transaction executed", { id: "multisig-action" });
      setLocalRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Execute failed", error);
      toast.error(error?.reason || error?.message || "Execution failed", {
        id: "multisig-action",
      });
    } finally {
      setActionId(null);
    }
  };

  const renderBody = () => {
    if (!provider || !managerAddress) {
      return (
        <tr>
          <td className="table-no-data" colSpan={7}>
            Connect to the correct network to view multisig transactions.
          </td>
        </tr>
      );
    }
    if (!effectiveIsOwner || !signer) {
      return (
        <tr>
          <td className="table-no-data" colSpan={7}>
            Connect with an authorized owner wallet to view multisig activity.
          </td>
        </tr>
      );
    }
    if (loading) {
      return (
        <tr>
          <td className="table-no-data" colSpan={7}>
            Loading multisig activity...
          </td>
        </tr>
      );
    }
    if (!filteredRows.length) {
      return (
        <tr>
          <td className="table-no-data" colSpan={7}>
            {activeTab === "executed"
              ? "No executed transactions found."
              : activeTab === "pending"
              ? "No pending transactions found."
              : "No transactions found."}
          </td>
        </tr>
      );
    }

    // console.log("📄 بيانات جدول Multisig من الكونتراكت:", filteredRows);

    return filteredRows.map((row) => (
      <tr key={row.transactionId}>
        <td>{row.transactionId}</td>
        <td>{row.functionName || "--"}</td>
        <td>
          {renderDetailsText(
            row.detailsText || parseArgs(row.functionName, row.data),
            row.detailsAddresses || [],
            addressNames,
            handleCopy
          )}
        </td>
        <td>
          {(() => {
            const fromLower = row.from?.toLowerCase?.();
            const fromName = fromLower ? addressNames[fromLower] : null;
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Tooltip
                  title={
                    <div
                      style={{
                        padding: "4px 0",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        minWidth: "200px",
                      }}
                    >
                      {fromName && (
                        <div
                          style={{
                            fontSize: "0.95rem",
                            fontWeight: 500,
                            color: "#fff",
                            marginBottom: "4px",
                          }}
                        >
                          {fromName}
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          justifyContent: "space-between",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.85rem",
                            color: "#bfc3d9",
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                          }}
                        >
                          {row.from || "--"}
                        </span>
                        {row.from && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(row.from);
                            }}
                            sx={{
                              color: "#fff",
                              padding: "4px",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                              },
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  }
                  placement="top"
                  arrow
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      lineHeight: 1.1,
                      cursor: "pointer",
                    }}
                  >
                    {fromName && (
                      <span
                        style={{
                          fontSize: "1rem",
                          color: "#bfc3d9",
                          marginBottom: 4,
                        }}
                      >
                        {fromName}
                      </span>
                    )}
                    <span style={{ fontSize: "0.75rem" }}>
                      {shorten(row.from)}
                    </span>
                  </div>
                </Tooltip>
              </div>
            );
          })()}
        </td>
        <td>
          <Tooltip
            title={
              row.confirmed && row.confirmed.length > 0 ? (
                <div style={{ padding: "8px" }}>
                  <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                    Confirmed by ({row.confirmed.length}):
                  </div>
                  {row.confirmed.map((addr, idx) => {
                    const lowerAddr = addr?.toLowerCase?.();
                    const name = lowerAddr ? addressNames[lowerAddr] : null;
                    return (
                      <div
                        key={idx}
                        style={{
                          marginBottom: "4px",
                          fontSize: "0.85rem",
                          lineHeight: "1.4",
                        }}
                      >
                        {name && (
                          <div style={{ fontWeight: "500", color: "#bfc3d9" }}>
                            {name}
                          </div>
                        )}
                        <div style={{ fontSize: "0.75rem", color: "#fff" }}>
                          {shorten(addr)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                "No confirmations yet"
              )
            }
            placement="top"
            arrow
          >
            <span style={{ cursor: "help" }}>
              {`${row.approvals}/${row.numConfirmations}`}
            </span>
          </Tooltip>
        </td>
        <td>
          {row.executed
            ? "Executed"
            : row.approvals === row.numConfirmations &&
              row.numConfirmations === 3
            ? "Ready"
            : "Pending"}
        </td>
        <td>
          {row.executed ? (
            <span className="text-muted" style={{ fontStyle: "italic" }}>
              Executed
            </span>
          ) : (
            <div className="d-flex gap-2 flex-wrap">
              {row.hasApproved ? (
                <button
                  type="button"
                  className="transaction-filter-btn"
                  disabled
                  style={{ opacity: 0.6, cursor: "not-allowed" }}
                >
                  Approved
                </button>
              ) : (
                <button
                  type="button"
                  className="transaction-filter-btn"
                  disabled={!row.canApprove || actionId === row.transactionId}
                  onClick={() => handleConfirm(row.transactionId)}
                >
                  {actionId === row.transactionId ? "Processing..." : "Approve"}
                </button>
              )}
              {row.canExecute && (
                <button
                  type="button"
                  className="transaction-filter-btn"
                  disabled={actionId === row.transactionId}
                  onClick={() => handleExecute(row.transactionId)}
                  style={{
                    borderColor: "#f8780c",
                    background: "rgba(248, 120, 12, 0.1)",
                    color: "#f8780c",
                  }}
                >
                  {actionId === row.transactionId ? "Processing..." : "Execute"}
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
    ));
  };

  return (
    <>
      <Card className="bg-live mb-4 p-2">
        <Card.Body>
          {/* <div className="d-flex justify-content-between align-items-center mb-3 w-100"> */}
          <div className="contract-summary-panel-head">
            <div className="d-flex align-items-center justify-space-between w-100">
              <div className="d-flex align-items-center gap-3">
                <h5 className="mb-0 min-w-250px">
                  Mint Transactions (Multisig)
                </h5>
                <div
                  className="transactions-filter-buttons gap-1"
                  style={{ marginBottom: 0 }}
                >
                  <button
                    type="button"
                    className={`transaction-filter-btn2 ${
                      activeTab === "all" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("all")}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`transaction-filter-btn2 ${
                      activeTab === "pending" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("pending")}
                  >
                    Pending
                  </button>
                  <button
                    type="button"
                    className={`transaction-filter-btn2 ${
                      activeTab === "executed" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("executed")}
                  >
                    Executed
                  </button>
                </div>
              </div>
              <small>
                Owners must approve each transaction before execution.
                <button
                  type="button"
                  className="summary-copy-btn ml-3"
                  onClick={() => setLocalRefresh((prev) => prev + 1)}
                >
                  <IoRefresh />
                </button>
                {effectiveIsOwner && (
                  <button
                    type="button"
                    className="summary-copy-btn1 ml-3"
                    onClick={() => setShowMintPanel((prev) => !prev)}
                  >
                    <span className="mr-3">
                      {showMintPanel ? "Hide Mint" : "Mint Shares"}
                    </span>
                    {showMintPanel ? <IoChevronDown /> : <IoChevronForward />}
                  </button>
                )}
              </small>
            </div>
            <div className="borderSpace borderSpaceTitle title-inbox mt-4 "></div>
          </div>
          {showMintPanel && effectiveIsOwner && (
            <div className="mt-3">
              <MintPanel
                signer={signer}
                currentUser={currentUser}
                adminAddresses={adminAddresses}
                onMinted={() => {
                  setLocalRefresh((prev) => prev + 1);
                }}
              />
            </div>
          )}
          {/* </div> */}
          <div className="table-responsive-wrapper">
            <table className="purchasing-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Function</th>
                  <th>Details</th>
                  <th>Requested By</th>
                  <th>Confirmations</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>{renderBody()}</tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
      {total > 0 && (
        <Pagination
          totalList={total}
          pages={totalPages}
          currentPage={page}
          setCurrentPage={setPage}
          itemsPerPage={PAGE_SIZE}
        />
      )}
    </>
  );
}

export default MultisigTransactions;
